import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import getGraphAssumptions from '@/services/GraphAssumptionsService'
import { useSuperStore } from '@/store/pinia/SuperStore'
import { useRetirementDataManager } from './RetirementCalculator/dataManager'
import { useRetirementCalculationService } from './RetirementCalculator/calculationService'
import { useRetirementHighchartsProcessor } from './RetirementCalculator/highchartsCalculatorDataProcessor'
import { useRetirementCalculatorProcessor } from './RetirementCalculator/calculatorDataProcessor'
import { useAssumptionsManager } from './RetirementCalculator/assumptionsManager'
import type { RetirementData, IncomeAsset, CareerBreak } from './RetirementCalculator/types'

export const useRetirementCalculatorStore = defineStore('RetirementCalculatorStore', () => {
  // Initialize managers
  const dataManager = useRetirementDataManager()
  const calculationService = useRetirementCalculationService()
  const highchartsProcessor = useRetirementHighchartsProcessor()
  const chartProcessor = useRetirementCalculatorProcessor()
  const assumptionsManager = useAssumptionsManager()

  // Store dependencies
  const superStore = useSuperStore()

  // Core state
  const userData = ref<RetirementData>(dataManager.createDefaultRetirementData())
  const partnerData = ref<RetirementData>(dataManager.createDefaultRetirementData())
  const userHatesMoney = ref(false)
  const incomeAssets = ref<IncomeAsset[]>([dataManager.createDefaultIncomeAsset()])
  const enablePartnerForm = ref(false)
  const displaySummary = ref(false)
  const graphLoading = ref(false)
  const graphData = ref<any>({})
  const noAgePension = ref(false)
  const disclaimer = ref('')

  // Assumptions state
  const defaultAssumptions = assumptionsManager.createDefaultAssumptions()
  const inflation = ref(defaultAssumptions.inflation)
  const wageGrowth = ref(defaultAssumptions.wageGrowth)
  const insurancePremium = ref(defaultAssumptions.insurancePremium)
  const annualAdminFees = ref(defaultAssumptions.annualAdminFees)
  const assumptionValues = ref(defaultAssumptions.assumptionValues)

  // Getters
  const getUserData = computed(() => userData.value)
  const getPartnerData = computed(() => partnerData.value)
  const getDisclaimer = computed(() => disclaimer.value)
  const getEnablePartnerForm = computed(() => enablePartnerForm.value)
  const getDisplaySummary = computed(() => displaySummary.value)
  const getIncomeAssetArray = computed(() => incomeAssets.value)
  const getGraphData = computed(() => graphData.value)
  const getGraphLoading = computed(() => graphLoading.value)
  const getInsurancePremium = computed(() => insurancePremium.value)
  const getAnnualAdminFees = computed(() => annualAdminFees.value)
  const getAssumptionValues = computed(() => assumptionValues.value)
  const getWageGrowth = computed(() => wageGrowth.value)
  const getIncomeStatsStatements = computed(() => graphData.value.income_stats_statements)
  const getUserHatesMoney = computed(() => userHatesMoney.value)
  const getNoAgePension = computed(() => noAgePension.value)
  const getInflation = computed(() => inflation.value)

  const getRetCalcAssetChartData = computed(() => {
    if (!graphData.value.asset) return []
    const seriesData = highchartsProcessor.mapAllRetCalcAssetsData(graphData.value, userData.value)
    return highchartsProcessor.reformatSeriesData(seriesData, userData.value, partnerData.value, enablePartnerForm.value)
  })

  // New Chart.js compatible data getter for Otivo
  const getRetCalcAssetChartDataForChartJs = computed(() => {
    if (!graphData.value.asset) return { labels: [], datasets: [] }
    return chartProcessor.mapAllRetCalcAssetsData(graphData.value, userData.value)
  })

  // Helper to get minimum super contribution rate
  const getMinSuperContributionRate = (): number | null => {
    return superStore.getSuperGuaranteeData?.minSuperGuarantee || null
  }

  // Check if SuperStore data is available
  const isSuperStoreDataAvailable = (): boolean => {
    return superStore.getSuperGuaranteeData?.minSuperGuarantee !== null
  }

  // Ensure SuperStore data is loaded
  const ensureSuperStoreData = async (): Promise<void> => {
    if (!isSuperStoreDataAvailable()) {
      // SuperStore auto-fetches on initialization, but we can trigger a manual fetch if needed
      // The SuperStore fetchSuperGuaranteeData is called on store initialization
      console.warn('SuperStore guarantee data not available yet')
      throw new Error('Super guarantee data is not available. Please ensure SuperStore is properly initialized.')
    }
  }

  // Local storage utilities
  const createLocalStorage = () => {
    window.localStorage.setItem('counter', '1')
  }

  const incrementCounter = () => {
    let count = Number(window.localStorage.getItem('counter'))
    count += 1
    window.localStorage.setItem('counter', count.toString())
  }

  // Core data management actions
  const updateUserDataKeyPair = (object: { field: string; value: any }) => {
    userData.value[object.field] = object.value
  }

  const updatePartnerDataKeyPair = (object: { field: string; value: any }) => {
    partnerData.value[object.field] = object.value
  }

  const updateUserPartTimeData = (object: { key: string; value: any }) => {
    userData.value.part_time[object.key] = object.value
  }

  const updatePartnerPartTimeData = (object: { key: string; value: any }) => {
    partnerData.value.part_time[object.key] = object.value
  }

  const setMinContributionRates = async (): Promise<void> => {
    try {
      await ensureSuperStoreData()
      const rate = getMinSuperContributionRate()
      if (rate !== null) {
        userData.value.contribution_rate = rate
        partnerData.value.contribution_rate = rate
      } else {
        throw new Error('Unable to get minimum super guarantee rate from SuperStore')
      }
    } catch (error) {
      console.error('Error setting minimum contribution rates:', error)
      throw error
    }
  }

  const togglePartnerForm = async (): Promise<void> => {
    const resetData = dataManager.resetRetirementData('partner')
    partnerData.value = resetData.partnerData
    try {
      const rate = getMinSuperContributionRate()
      if (rate !== null) {
        partnerData.value.contribution_rate = rate
      } else {
        console.warn('Super guarantee data not available when toggling partner form')
      }
    } catch (error) {
      console.error('Error setting partner contribution rate:', error)
    }
    enablePartnerForm.value = !enablePartnerForm.value
  }

  const refreshRetCalc = async (): Promise<void> => {
    const resetData = dataManager.resetRetirementData('all')
    userData.value = resetData.userData
    partnerData.value = resetData.partnerData
    userHatesMoney.value = false
    incomeAssets.value = [dataManager.createDefaultIncomeAsset()]
    if (enablePartnerForm.value) enablePartnerForm.value = false

    // Clear any existing errors
    const { useRetirementCalculatorErrorStore } = await import('./RetirementCalculatorErrorStore')
    const errorsStore = useRetirementCalculatorErrorStore()
    errorsStore.resetErrors()
    displaySummary.value = false
    noAgePension.value = false
    resetAssumptions()
    try {
      await setMinContributionRates()
    } catch (error) {
      console.error('Error setting contribution rates during refresh:', error)
    }
    window.scrollTo(0, 0)
  }

  const refreshData = (target = 'all') => {
    const resetData = dataManager.resetRetirementData(target as 'user' | 'partner' | 'all')
    if ('userData' in resetData) userData.value = resetData.userData
    if ('partnerData' in resetData) partnerData.value = resetData.partnerData
    if (target !== 'partner') {
      incomeAssets.value = [{ ...dataManager.createDefaultIncomeAsset(), shared: false }]
    }
  }

  // Income assets management
  const updateIncomeAsset = (payload: { index: number; field: string; value: any }) => {
    incomeAssets.value[payload.index][payload.field] = payload.value
  }

  const addIncomeAsset = () => {
    incomeAssets.value.push(dataManager.createDefaultIncomeAsset())
  }

  const removeIncomeAsset = (index: number) => {
    incomeAssets.value.splice(index, 1)
  }

  // Career breaks management
  const updateCareerBreak = (payload: { owner: string; index: number; field: string; value: any }) => {
    const target = payload.owner === 'userData' ? userData.value : partnerData.value
    target.career_breaks[payload.index][payload.field] = payload.value
  }

  const addCareerBreak = (owner: string) => {
    const target = owner === 'userData' ? userData.value : partnerData.value
    target.career_breaks.push(dataManager.createDefaultCareerBreak())
  }

  const removeCareerBreak = (payload: { owner: string; index: number }) => {
    const target = payload.owner === 'userData' ? userData.value : partnerData.value
    target.career_breaks.splice(payload.index, 1)
    // Note: Error handling will be handled by the errors store
  }

  // Assumptions management
  const resetAssumptions = () => {
    const defaults = assumptionsManager.resetToDefaults()
    inflation.value = defaults.inflation
    wageGrowth.value = defaults.wageGrowth
    insurancePremium.value = defaults.insurancePremium
    annualAdminFees.value = defaults.annualAdminFees
    assumptionValues.value = defaults.assumptionValues
  }

  const updateInsurancePremium = (value: number) => {
    insurancePremium.value = value
  }

  const updateAnnualAdminFees = (value: number) => {
    annualAdminFees.value = value
  }

  const updateRetCalcAssumptionValue = (params: { type: string; value: any; label: string }) => {
    assumptionValues.value = assumptionsManager.updateAssumptionValue(assumptionValues.value, params)
  }

  const setRetCalcTaxOnEarnings = (params: { type: string; value: number }) => {
    assumptionValues.value = assumptionsManager.updateTaxOnEarnings(assumptionValues.value, params)
  }

  const setRetCalcInvestReturn = (params: { type: string; value: number }) => {
    assumptionValues.value = assumptionsManager.updateInvestmentReturn(assumptionValues.value, params)
  }

  const setRetCalcInvestAdminFee = (params: { type: string; value: number }) => {
    assumptionValues.value = assumptionsManager.updateAdminFee(assumptionValues.value, params)
  }

  const setInvestmentWageGrowth = (value: number) => {
    wageGrowth.value = value
  }

  const setInvestInflationRate = (value: number) => {
    inflation.value = value
  }

  // Graph and calculation actions
  const setGraphLoading = (value: boolean) => {
    graphLoading.value = value
  }

  const setRetirementCalcData = (res: any) => {
    graphData.value = res
  }

  const hidePension = (bool: boolean) => {
    noAgePension.value = bool
  }

  const setDisclaimer = (disclaimerText: string) => {
    disclaimer.value = disclaimerText
  }

  const userLikesMoney = async () => {
    graphLoading.value = true
    noAgePension.value = false
    userHatesMoney.value = false
    return await getRetirementCalculatorData()
  }

  const setNoAgePension = async () => {
    graphLoading.value = true
    noAgePension.value = true
    return await getRetirementCalculatorData()
  }

  // Validation function
  const validateAndCalculate = async () => {
    graphLoading.value = true

    const { useRetirementCalculatorErrorStore } = await import('./RetirementCalculatorErrorStore')
    const errorsStore = useRetirementCalculatorErrorStore()

    const valid = await errorsStore.validateRetirementCalculatorFields(
      userData.value,
      partnerData.value,
      enablePartnerForm.value,
      incomeAssets.value
    )

    if (!valid) {
      graphLoading.value = false
      return false
    }

    return await getRetirementCalculatorData()
  }

  // Main calculation function
  const getRetirementCalculatorData = async () => {
    graphLoading.value = true
    try {
      // Ensure SuperStore data is available before calculation
      await ensureSuperStoreData()

      // Validate that contribution rates are set
      if (userData.value.contribution_rate === null || userData.value.contribution_rate === undefined) {
        console.warn('User contribution rate is null, attempting to set minimum rates')
        await setMinContributionRates()
      }

      if (enablePartnerForm.value && (partnerData.value.contribution_rate === null || partnerData.value.contribution_rate === undefined)) {
        console.warn('Partner contribution rate is null, attempting to set minimum rates')
        await setMinContributionRates()
      }

      const assumptions = {
        insurancePremium: insurancePremium.value,
        annualAdminFees: annualAdminFees.value,
        assumptionValues: assumptionValues.value,
        inflation: inflation.value,
        wageGrowth: wageGrowth.value,
        noAgePension: noAgePension.value,
      }

      const payload = calculationService.buildCalculationPayload(
        userData.value,
        partnerData.value,
        incomeAssets.value,
        assumptions,
        enablePartnerForm.value
      )

      const res = await calculationService.performCalculation(payload)
      incrementCounter()
      setRetirementCalcData(res.data)
      graphLoading.value = false
      window.scrollTo(0, 0)
      displaySummary.value = true

      // Check for age pension opt-out
      const seriesData = highchartsProcessor.mapAllRetCalcAssetsData(graphData.value, userData.value)
      const doesUserHateMoney = seriesData[1]?.data?.find(
        (el: any) => el.itemData.pensionSource === 'User has opted not to include the age pension.',
      )
      userHatesMoney.value = !!doesUserHateMoney

      return true
    } catch (err: any) {
      graphLoading.value = false
      console.error('Retirement calculation error:', err)
      // Note: Error handling will be managed by the errors store
      return false
    }
  }

  // External service calls
  const fetchPreservationAgesForRetCalc = async () => {
    // Note: This will integrate with the retirement ages store
    try {
      const res = await calculationService.fetchPreservationAges()
      return res.data
    } catch (err) {
      console.error('Error fetching preservation ages:', err)
      throw err
    }
  }

  const getPreservationAgeForBirthDate = async (birthDate: string, isUser = true) => {
    try {
      return await calculationService.getPreservationAgeForBirthDate(birthDate, isUser)
    } catch (err) {
      console.error('Error getting preservation age for birth date:', err)
      throw err
    }
  }

  const fetchDisclaimer = async () => {
    try {
      const response = await getGraphAssumptions('CFS_ASSUMPTIONS_AND_LIMITATIONS')
      if (response.data) {
        setDisclaimer(response.data.data.assumptions)
      }
    } catch (err) {
      console.warn('error reading disclaimer data', err)
    }
  }

  const downloadPDF = async (pngs: any[]) => {
    let incomeAssetsData = null
    if (incomeAssets.value.length > 0 && incomeAssets.value[0].type !== null) {
      incomeAssetsData = incomeAssets.value
    }

    const payload = {
      userData: getUserData.value,
      partnerData: getPartnerData.value,
      incomeAssets: incomeAssetsData,
      incomeStatsStatements: getIncomeStatsStatements.value,
      graphs: pngs,
      assumptionValues: getAssumptionValues.value,
      noAgePension: noAgePension.value,
    }

    return calculationService.downloadPDF(payload)
  }

  const downloadPDFIphoneTest = async (payload: any) => {
    return calculationService.downloadPDF(payload)
  }

  // Error handling helper (will integrate with errors store)
  const addErrorFromBackend = (errDataField: string) => {
    // Note: This will be handled by the errors store
    console.log('Backend error for field:', errDataField)
  }

  return {
    // State
    userData,
    partnerData,
    userHatesMoney,
    incomeAssets,
    enablePartnerForm,
    displaySummary,
    graphLoading,
    graphData,
    inflation,
    wageGrowth,
    insurancePremium,
    annualAdminFees,
    assumptionValues,
    noAgePension,
    disclaimer,

    // Getters
    getUserData,
    getPartnerData,
    getDisclaimer,
    getEnablePartnerForm,
    getDisplaySummary,
    getIncomeAssetArray,
    getGraphData,
    getGraphLoading,
    getInsurancePremium,
    getAnnualAdminFees,
    getAssumptionValues,
    getWageGrowth,
    getIncomeStatsStatements,
    getUserHatesMoney,
    getNoAgePension,
    getInflation,
    getRetCalcAssetChartData,
    getRetCalcAssetChartDataForChartJs,

    // Actions
    createLocalStorage,
    incrementCounter,
    updateUserDataKeyPair,
    updatePartnerDataKeyPair,
    updateUserPartTimeData,
    updatePartnerPartTimeData,
    setMinContributionRates,
    togglePartnerForm,
    refreshRetCalc,
    refreshData,
    updateIncomeAsset,
    addIncomeAsset,
    removeIncomeAsset,
    updateCareerBreak,
    addCareerBreak,
    removeCareerBreak,
    resetAssumptions,
    updateInsurancePremium,
    updateAnnualAdminFees,
    updateRetCalcAssumptionValue,
    setRetCalcTaxOnEarnings,
    setRetCalcInvestReturn,
    setRetCalcInvestAdminFee,
    setInvestmentWageGrowth,
    setInvestInflationRate,
    setGraphLoading,
    setRetirementCalcData,
    hidePension,
    setDisclaimer,
    userLikesMoney,
    setNoAgePension,
    validateAndCalculate,
    getRetirementCalculatorData,
    fetchPreservationAgesForRetCalc,
    getPreservationAgeForBirthDate,
    fetchDisclaimer,
    downloadPDF,
    downloadPDFIphoneTest,
    addErrorFromBackend,
    getMinSuperContributionRate,
    isSuperStoreDataAvailable,
    ensureSuperStoreData,
  }
})
